import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus,
  Mail,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Play,
  Square,
  Eye,
  Trash2,
  Filter,
  Calendar,
  Send,
  AlertCircle
} from 'lucide-react';
import api, { bulkEmailAPI, leadsAPI } from '../services/api';
import SettingsRequiredModal from '../components/SettingsRequiredModal';

// Global helper functions
const getStatusColor = (status) => {
  switch (status) {
    case 'draft': return 'bg-gray-100 text-gray-800';
    case 'sending': return 'bg-blue-100 text-blue-800';
    case 'completed': return 'bg-green-100 text-green-800';
    case 'failed': return 'bg-red-100 text-red-800';
    case 'cancelled': return 'bg-yellow-100 text-yellow-800';
    case 'pending': return 'bg-gray-100 text-gray-800';
    case 'sent': return 'bg-green-100 text-green-800';
    case 'unsubscribed': return 'bg-orange-100 text-orange-800';
    case 'skipped': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'draft': return <Clock size={16} />;
    case 'sending': return <Send size={16} />;
    case 'completed': return <CheckCircle size={16} />;
    case 'failed': return <XCircle size={16} />;
    case 'cancelled': return <Square size={16} />;
    case 'pending': return <Clock size={16} />;
    case 'sent': return <CheckCircle size={16} />;
    case 'unsubscribed': return <XCircle size={16} />;
    case 'skipped': return <Square size={16} />;
    default: return <Clock size={16} />;
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'draft': return 'Draft';
    case 'sending': return 'Sending';
    case 'completed': return 'Completed';
    case 'failed': return 'Failed';
    case 'cancelled': return 'Cancelled';
    case 'pending': return 'Pending';
    case 'sent': return 'Sent';
    case 'unsubscribed': return 'Unsubscribed';
    case 'skipped': return 'Skipped';
    default: return status;
  }
};

const BulkEmail = () => {
  const { user } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: 'all',
    recipientSize: 'all',
    sortBy: 'newest'
  });

  const queryClient = useQueryClient();

  // SMTP configuration probe (gate campaign starts when not configured)
  const { data: smtpSettings } = useQuery(
    ['smtp-settings'],
    () => api.get('/smtp/settings').then((r) => r.data),
    { staleTime: 60 * 1000, retry: false }
  );
  const isSmtpConfigured = !!smtpSettings?.isConfigured;
  const canManageSettings = user?.permissions?.manage_smtp_settings !== false;
  const [settingsRequiredOpen, setSettingsRequiredOpen] = useState(false);

  // Kampanyaları getir
  const { data: campaignsData, isLoading, error } = useQuery(
    ['bulk-email-campaigns', filters],
    () => bulkEmailAPI.getCampaigns({
      status: filters.status,
      dateRange: filters.dateRange,
      recipientSize: filters.recipientSize,
      sortBy: filters.sortBy
    }),
    {
      refetchInterval: 10000, // 10 saniyede bir güncelle
      onSuccess: (data) => {
      },
      onError: (error) => {
        console.error('Bulk email campaigns error:', error);
      }
    }
  );

  // Kampanya oluştur
  const createCampaignMutation = useMutation(
    bulkEmailAPI.createCampaign,
    {
      onSuccess: () => {
        toast.success('Campaign created successfully');
        setShowCreateModal(false);
        queryClient.invalidateQueries('bulk-email-campaigns');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'An error occurred while creating the campaign');
      }
    }
  );

  // Kampanya başlat
  const startCampaignMutation = useMutation(
    bulkEmailAPI.startCampaign,
    {
      onSuccess: () => {
        toast.success('Campaign started successfully');
        queryClient.invalidateQueries('bulk-email-campaigns');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'An error occurred while starting the campaign');
      }
    }
  );

  // Kampanya iptal et
  const cancelCampaignMutation = useMutation(
    bulkEmailAPI.cancelCampaign,
    {
      onSuccess: () => {
        toast.success('Campaign cancelled successfully');
        queryClient.invalidateQueries('bulk-email-campaigns');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'An error occurred while cancelling the campaign');
      }
    }
  );

  // Kampanya sil
  const deleteCampaignMutation = useMutation(
    bulkEmailAPI.deleteCampaign,
    {
      onSuccess: () => {
        toast.success('Campaign deleted successfully');
        queryClient.invalidateQueries('bulk-email-campaigns');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'An error occurred while deleting the campaign');
      }
    }
  );



  const campaigns = campaignsData?.data?.campaigns || [];

  // Kampanya başlat
  const handleStartCampaign = (campaignId) => {
    if (!isSmtpConfigured) {
      setSettingsRequiredOpen(true);
      return;
    }
    startCampaignMutation.mutate(campaignId);
  };

  // Kampanya iptal et
  const handleCancelCampaign = (campaignId) => {
    if (window.confirm('Are you sure you want to cancel this campaign?')) {
      cancelCampaignMutation.mutate(campaignId);
    }
  };

  // Kampanya sil
  const handleDeleteCampaign = (campaignId, campaignName) => {
    if (window.confirm(`Are you sure you want to delete the campaign "${campaignName}"? This action cannot be undone.`)) {
      deleteCampaignMutation.mutate(campaignId);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <SettingsRequiredModal
        isOpen={settingsRequiredOpen}
        onClose={() => setSettingsRequiredOpen(false)}
        type="email"
        canManageSettings={canManageSettings}
      />
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Bulk Email</h1>
          <p className="text-gray-600 mt-2">Manage your email campaigns</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center"
          >
            <Plus size={20} className="mr-2" />
            New Campaign
          </button>
        </div>
      </div>

      {/* Filtreler */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Filter size={20} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                <p className="text-sm text-gray-500">Filter campaigns by status</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {campaigns.length} campaigns found
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Durum Filtresi */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <div className="relative">
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                >
                  <option value="all">📋 All Statuses</option>
                  <option value="draft">📝 Draft</option>
                  <option value="sending">📤 Sending</option>
                  <option value="completed">✅ Completed</option>
                  <option value="failed">❌ Failed</option>
                  <option value="cancelled">⏹️ Cancelled</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Tarih Filtresi */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Date Range
              </label>
              <div className="relative">
                <select
                  value={filters.dateRange}
                  onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
                  className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                >
                  <option value="all">📅 All Time</option>
                  <option value="today">📅 Today</option>
                  <option value="week">📅 This Week</option>
                  <option value="month">📅 This Month</option>
                  <option value="quarter">📅 This Quarter</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Alıcı Sayısı Filtresi */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Recipient Count
              </label>
              <div className="relative">
                <select
                  value={filters.recipientSize}
                  onChange={(e) => setFilters({ ...filters, recipientSize: e.target.value })}
                  className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                >
                  <option value="all">👥 All Sizes</option>
                  <option value="small">👥 Small (1-50)</option>
                  <option value="medium">👥 Medium (51-200)</option>
                  <option value="large">👥 Large (200+)</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Sıralama */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Sort By
              </label>
              <div className="relative">
                <select
                  value={filters.sortBy}
                  onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                  className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                >
                  <option value="newest">🕒 Newest</option>
                  <option value="oldest">🕒 Oldest</option>
                  <option value="name">📝 By Name</option>
                  <option value="recipients">👥 By Recipient Count</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Aktif Filtreler */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">Active Filters:</span>
                {filters.status !== 'all' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {filters.status === 'draft' && '📝 Draft'}
                    {filters.status === 'sending' && '📤 Sending'}
                    {filters.status === 'completed' && '✅ Completed'}
                    {filters.status === 'failed' && '❌ Failed'}
                    {filters.status === 'cancelled' && '⏹️ Cancelled'}
                    <button
                      onClick={() => setFilters({ ...filters, status: 'all' })}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filters.dateRange !== 'all' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {filters.dateRange === 'today' && '📅 Today'}
                    {filters.dateRange === 'week' && '📅 This Week'}
                    {filters.dateRange === 'month' && '📅 This Month'}
                    {filters.dateRange === 'quarter' && '📅 This Quarter'}
                    <button
                      onClick={() => setFilters({ ...filters, dateRange: 'all' })}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filters.recipientSize !== 'all' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {filters.recipientSize === 'small' && '👥 Small'}
                    {filters.recipientSize === 'medium' && '👥 Medium'}
                    {filters.recipientSize === 'large' && '👥 Large'}
                    <button
                      onClick={() => setFilters({ ...filters, recipientSize: 'all' })}
                      className="ml-2 text-purple-600 hover:text-purple-800"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>

              {(filters.status !== 'all' || filters.dateRange !== 'all' || filters.recipientSize !== 'all') && (
                <button
                  onClick={() => setFilters({ status: 'all', dateRange: 'all', recipientSize: 'all', sortBy: 'newest' })}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear All Filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Kampanya Listesi */}
      {error && (
        <div className="card text-center py-12 bg-red-50 border-red-200">
          <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-red-900 mb-2">An error occurred</h3>
          <p className="text-red-600 mb-4">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Refresh Page
          </button>
        </div>
      )}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="card text-center py-12">
          <Mail size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns yet</h3>
          <p className="text-gray-600 mb-4">Start creating your first email campaign</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            Create First Campaign
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {campaigns.map((campaign) => (
            <div key={campaign._id} className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-all duration-200 shadow-sm hover:shadow-md">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-sm">
                      <Mail size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-1">{campaign.name}</h3>
                      <p className="text-gray-600 mb-2">{campaign.subject}</p>
                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <span>Created: {new Date(campaign.createdAt).toLocaleDateString('en-US')}</span>
                        {campaign.scheduledAt && (
                          <span>Scheduled: {new Date(campaign.scheduledAt).toLocaleDateString('en-US')}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    {/* Durum */}
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${getStatusColor(campaign.status)}`}>
                      {getStatusIcon(campaign.status)}
                      <span className="ml-2">{getStatusText(campaign.status)}</span>
                    </span>
                  </div>
                </div>

                {/* İstatistikler */}
                <div className="grid grid-cols-6 gap-4 pt-4 border-t border-gray-100">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{campaign.totalRecipients}</div>
                    <div className="text-xs text-gray-500">Recipients</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{campaign.sentCount || 0}</div>
                    <div className="text-xs text-gray-500">Sent</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{campaign.failedCount || 0}</div>
                    <div className="text-xs text-gray-500">Failed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{campaign.unsubscribeCount || 0}</div>
                    <div className="text-xs text-gray-500">Unsub.</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {campaign.engagement?.openRate ?? 0}%
                    </div>
                    <div className="text-xs text-gray-500">
                      Open rate
                      {campaign.engagement?.uniqueOpens > 0 && (
                        <span className="block text-[10px] text-gray-400">({campaign.engagement.uniqueOpens} opens)</span>
                      )}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-600">
                      {campaign.engagement?.clickRate ?? 0}%
                    </div>
                    <div className="text-xs text-gray-500">
                      Click rate
                      {campaign.engagement?.uniqueClicks > 0 && (
                        <span className="block text-[10px] text-gray-400">({campaign.engagement.uniqueClicks} clicks)</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* İlerleme Çubuğu */}
                {campaign.status === 'sending' && campaign.totalRecipients > 0 && ((campaign.sentCount + campaign.failedCount + campaign.unsubscribeCount) < campaign.totalRecipients) && (
                  <div className="mt-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{Math.round(((campaign.sentCount + campaign.failedCount + campaign.unsubscribeCount) / campaign.totalRecipients) * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${((campaign.sentCount + campaign.failedCount + campaign.unsubscribeCount) / campaign.totalRecipients) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Aksiyon Butonları */}
                <div className="flex items-center justify-end space-x-3 mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setSelectedCampaign(campaign)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                  >
                    <Eye size={16} className="mr-1" />
                    Details
                  </button>

                  {campaign.status === 'draft' && (
                    <button
                      onClick={() => handleStartCampaign(campaign._id)}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center"
                    >
                      <Play size={16} className="mr-1" />
                      Start
                    </button>
                  )}

                  {campaign.status === 'sending' && (
                    <button
                      onClick={() => handleCancelCampaign(campaign._id)}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center"
                    >
                      <Square size={16} className="mr-1" />
                      Cancel
                    </button>
                  )}

                  {/* Silme Butonu - Sadece taslak, tamamlanmış, başarısız ve iptal edilmiş kampanyalar için */}
                  {(campaign.status === 'draft' || campaign.status === 'completed' || campaign.status === 'failed' || campaign.status === 'cancelled') && (
                    <button
                      onClick={() => handleDeleteCampaign(campaign._id, campaign.name)}
                      disabled={deleteCampaignMutation.isLoading}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center"
                    >
                      <Trash2 size={16} className="mr-1" />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modaller */}
      {showCreateModal && (
        <CreateCampaignModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={createCampaignMutation.mutate}
          isLoading={createCampaignMutation.isLoading}
        />
      )}

      {selectedCampaign && (
        <CampaignDetailModal
          campaign={selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
        />
      )}
    </div>
  );
};

// Kampanya Oluşturma Modal
const CreateCampaignModal = ({ onClose, onSubmit, isLoading }) => {
  const { user: currentUser } = useAuth();

  // Organization bilgilerini çek
  const { data: organization } = useQuery(
    ['organization', currentUser?.organization],
    () => leadsAPI.getOrganization(currentUser?.organization),
    {
      enabled: !!currentUser?.organization,
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: true
    }
  );

  // Organization bilgilerini kullan
  const SENDER_NAME = organization?.data?.organization?.name || 'CRM System';
  const COMPANY_NAME = organization?.data?.organization?.name || 'Your Company';
  const COMPANY_EMAIL = organization?.data?.organization?.email || 'info@yourcompany.com';
  const COMPANY_WEBSITE = organization?.data?.organization?.domain ? `https://${organization?.data?.organization?.domain}` : 'https://yourcompany.com';

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    message: '',
    attachments: [],
    filters: {
      status: [],
      source: [],
      priority: [],
      hasEmail: true
    },
    scheduledAt: ''
  });
  const [recipientCount, setRecipientCount] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);

  // Alıcı sayısını hesapla
  const calculateRecipientCount = async (filters) => {
    try {
      setIsCalculating(true);
      const response = await fetch('/api/bulk-email/calculate-recipients', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ filters })
      });

      if (response.ok) {
        const data = await response.json();
        setRecipientCount(data.count);
      } else {
        setRecipientCount(0);
      }
    } catch (error) {
      console.error('Error calculating recipient count:', error);
      setRecipientCount(0);
    } finally {
      setIsCalculating(false);
    }
  };

  // AI Mail hazırlama fonksiyonu - Lead Detail'daki gibi
  const generateAIContent = async () => {
    if (!formData.subject.trim()) {
      toast.error('Please enter a subject/topic first');
      return;
    }

    if (formData.subject.trim().length < 3) {
      toast.error('Subject must be at least 3 characters long');
      return;
    }

    setIsGeneratingAI(true);
    toast.loading('Generating GPT-4 email content...');

    try {
      // Server proxy üzerinden OpenAI API çağrısı yap
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Please log in to use AI features.');
      }

      // AI prompt oluştur
      const prompt = `⚠️ CRITICAL: You are ONLY allowed to write the email content. DO NOT write greetings, closings, signatures, or contact information.

First, create a professional and engaging email subject line based on this input: "${formData.subject}"

Company Information:
- Company Name: ${COMPANY_NAME}
- Sender Name: ${SENDER_NAME}
- Company Email: ${COMPANY_EMAIL}
- Company Website: ${COMPANY_WEBSITE}

Subject Line Requirements:
1. Make it professional and engaging
2. Include the company name if available
3. Make it specific to the topic mentioned
4. Keep it concise (under 60 characters)
5. Use proper capitalization

Then, write a professional bulk email message that directly relates to and expands on the improved subject line.

Email Content Requirements:
1. The email content MUST directly address and expand on the improved subject line
2. Use the subject as the main topic/focus of the email
3. Make the content relevant to the specific subject mentioned
4. Keep it professional but friendly
5. Make it engaging for bulk recipients
6. Keep it concise (2-3 paragraphs maximum)
7. End with a clear call to action related to the subject
8. Use the company information provided above for professional context
9. DO NOT include any greeting (Dear, Hello, Hi, etc.)
10. DO NOT include any closing phrases like "Best regards", "Regards", "Sincerely" - just end with the call to action
11. DO NOT include the sender's name or contact information
12. DO NOT include email signature - the system will add it automatically
13. CRITICAL: Never write "Best regards", "Regards", "Sincerely" or any similar closing phrases
14. CRITICAL: Never include sender name, email, or website in the content
15. CRITICAL: End the email directly with the call to action, nothing else

Format your response exactly like this:
SUBJECT: [Your improved subject line here]
CONTENT: [Your email content here]

⚠️ FINAL WARNING: 
- DO NOT write "Best regards", "Regards", "Sincerely" or any closing
- DO NOT write sender name, email, or website
- DO NOT write any signature
- End directly with your call to action
- The system will add the signature automatically

Example: If input is "tomorrow event", create a subject like "Tomorrow's Event at ${COMPANY_NAME}" and then write the email content.`;

      // OpenAI API çağrısı yap
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5002/api'}/openai/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          model: 'gpt-4',
          messages: [
            {
              role: 'system',
              content: `You are an expert professional email writer with deep understanding of business communication. Your task is to first create an improved, professional subject line based on the user input, then write email content that directly relates to and expands on that improved subject line. The content must be focused on the specific subject, not generic. Always use the subject as the main topic and make every sentence relevant to it.`
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 400,
          temperature: 0.6
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenAI API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const aiResponse = data.choices[0].message.content;

      // AI yanıtını parse et
      let improvedSubject = formData.subject; // Varsayılan olarak orijinal subject'i kullan
      let emailContent = aiResponse; // Varsayılan olarak tüm yanıtı kullan

      // SUBJECT: ve CONTENT: formatını kontrol et
      if (aiResponse.includes('SUBJECT:') && aiResponse.includes('CONTENT:')) {
        const subjectMatch = aiResponse.match(/SUBJECT:\s*(.+?)(?=\n|CONTENT:)/i);
        const contentMatch = aiResponse.match(/CONTENT:\s*(.+)/is);

        if (subjectMatch && contentMatch) {
          improvedSubject = subjectMatch[1].trim();
          emailContent = contentMatch[1].trim();
        }
      }

      // Hem subject'i hem de message'ı güncelle
      setFormData(prev => ({
        ...prev,
        subject: improvedSubject,
        message: emailContent
      }));

      toast.dismiss();
      toast.success('GPT-4 email content generated successfully!');
    } catch (error) {
      console.error('Content generation error:', error);
      toast.dismiss();
      toast.error('Failed to generate content');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Filtreler değiştiğinde alıcı sayısını güncelle
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculateRecipientCount(formData.filters);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.filters]);

  // Attachment işleme fonksiyonları
  const handleFileUpload = (files) => {
    const newFiles = Array.from(files).map(file => ({
      name: file.name,
      type: file.type,
      size: file.size,
      file: file
    }));

    setFormData({
      ...formData,
      attachments: [...(formData.attachments || []), ...newFiles]
    });

    toast.success(`${newFiles.length} file(s) added`);
    setShowAttachmentModal(false);
  };

  const removeAttachment = (index) => {
    const newAttachments = formData.attachments.filter((_, i) => i !== index);
    setFormData({ ...formData, attachments: newAttachments });
    toast.success('Attachment removed');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Attachments'ları base64'e çevir
    if (formData.attachments && formData.attachments.length > 0) {
      const processAttachments = async () => {
        try {
          const processedAttachments = await Promise.all(
            formData.attachments.map(async (fileObj) => {
              return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => {
                  const base64Content = reader.result.split(',')[1];
                  resolve({
                    name: fileObj.name,
                    type: fileObj.type,
                    content: base64Content,
                    size: fileObj.size
                  });
                };
                reader.readAsDataURL(fileObj.file);
              });
            })
          );

          const emailDataWithAttachments = {
            ...formData,
            attachments: processedAttachments
          };

          onSubmit(emailDataWithAttachments);
        } catch (error) {
          console.error('Error processing attachments:', error);
          toast.error('Error processing attachments');
        }
      };

      processAttachments();
    } else {
      onSubmit(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Create New Campaign</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircle size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Campaign Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Subject
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter a topic (e.g., 'tomorrow event', 'new product launch') and AI will generate both subject and content..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Content
            </label>
            <div className="space-y-3">
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => generateAIContent()}
                  disabled={isGeneratingAI}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGeneratingAI ? (
                    <span className="animate-spin mr-2">⏳</span>
                  ) : (
                    <span className="mr-2">✨</span>
                  )}
                  {isGeneratingAI ? 'Generating...' : 'AI Generate'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAttachmentModal(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 flex items-center space-x-2"
                >
                  📎 Add Attachments
                </button>
              </div>

              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={8}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="AI will generate content based on the subject above. You can also manually edit the content here..."
                required
              />

              {/* Attachment Preview */}
              {formData.attachments && formData.attachments.length > 0 && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">📎 Attachments ({formData.attachments.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {formData.attachments.map((file, index) => (
                      <div key={index} className="flex items-center space-x-2 bg-white px-3 py-2 rounded-lg border">
                        <span className="text-sm text-gray-600">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Lead Filtreleme Bölümü */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Lead Filtering</h3>
            <p className="text-sm text-gray-600 mb-4">Determine which leads will be included in this campaign</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Durum Filtresi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lead Status
                </label>
                <div className="space-y-2">
                  {['new', 'contacted', 'proposal_sent', 'negotiation', 'won', 'lost'].map((status) => (
                    <label key={status} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.filters.status.includes(status)}
                        onChange={(e) => {
                          const newStatus = e.target.checked
                            ? [...formData.filters.status, status]
                            : formData.filters.status.filter(s => s !== status);
                          setFormData({
                            ...formData,
                            filters: { ...formData.filters, status: newStatus }
                          });
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {status === 'new' && '🆕 New'}
                        {status === 'contacted' && '📞 Contact Made'}
                        {status === 'proposal_sent' && '📋 Quote Given'}
                        {status === 'negotiation' && '🤝 Negotiation'}
                        {status === 'won' && '🎯 Won'}
                        {status === 'lost' && '❌ Lost'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Kaynak Filtresi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lead Source
                </label>
                <div className="space-y-2">
                  {['website', 'phone', 'email', 'social_media', 'business_card', 'referral', 'google_ads', 'other'].map((source) => (
                    <label key={source} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.filters.source.includes(source)}
                        onChange={(e) => {
                          const newSource = e.target.checked
                            ? [...formData.filters.source, source]
                            : formData.filters.source.filter(s => s !== source);
                          setFormData({
                            ...formData,
                            filters: { ...formData.filters, source: newSource }
                          });
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {source === 'website' && '🌐 Website'}
                        {source === 'phone' && '📞 Phone'}
                        {source === 'email' && '📧 Email'}
                        {source === 'social_media' && '📱 Social Media'}
                        {source === 'business_card' && '📇 Business Card'}
                        {source === 'referral' && '👥 Referral'}
                        {source === 'google_ads' && '🎯 Google Ads'}
                        {source === 'other' && '📌 Other'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Öncelik Filtresi */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority Level
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {['low', 'medium', 'high'].map((priority) => (
                  <label key={priority} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.filters.priority.includes(priority)}
                      onChange={(e) => {
                        const newPriority = e.target.checked
                          ? [...formData.filters.priority, priority]
                          : formData.filters.priority.filter(p => p !== priority);
                        setFormData({
                          ...formData,
                          filters: { ...formData.filters, priority: newPriority }
                        });
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {priority === 'low' && '🟢 Low'}
                      {priority === 'medium' && '🟡 Medium'}
                      {priority === 'high' && '🔴 High'}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* E-posta Kontrolü */}
            <div className="mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.filters.hasEmail}
                  onChange={(e) => setFormData({
                    ...formData,
                    filters: { ...formData.filters, hasEmail: e.target.checked }
                  })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  📧 Only include leads with email addresses
                </span>
              </label>
            </div>

            {/* Alıcı Sayısı Önizlemesi */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-blue-900">📊 Recipient Count</h4>
                  <p className="text-xs text-blue-700 mt-1">
                    Number of leads that will be included in this campaign based on selected filters
                  </p>
                </div>
                <div className="text-right">
                  {isCalculating ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      <span className="text-sm text-blue-600">Calculating...</span>
                    </div>
                  ) : (
                    <div className="text-2xl font-bold text-blue-600">
                      {recipientCount}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Scheduled Date (Optional)
            </label>
            <input
              type="datetime-local"
              value={formData.scheduledAt}
              onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary flex items-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Plus size={16} className="mr-2" />
                  Create Campaign
                </>
              )}
            </button>
          </div>
        </form>

        {/* Attachment Modal */}
        {showAttachmentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">📎 Add Attachments</h3>
                <button
                  onClick={() => setShowAttachmentModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer block"
                  >
                    <div className="text-gray-600 mb-2">
                      <span className="text-2xl">📎</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      <span className="text-blue-600 hover:text-blue-800 font-medium">
                        Click to upload
                      </span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      PDF, DOC, DOCX, JPG, PNG, GIF (max 10MB each)
                    </p>
                  </label>
                </div>

                <div className="text-xs text-gray-500 text-center">
                  Supported formats: PDF, DOC, DOCX, JPG, PNG, GIF
                  <br />
                  Maximum file size: 10MB per file
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Kampanya Detay Modal
const CampaignDetailModal = ({ campaign, onClose }) => {
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  useEffect(() => {
    const fetchRecipients = async () => {
      setLoadingRecipients(true);
      try {
        const response = await bulkEmailAPI.getRecipients(campaign._id);
        setRecipients(response.data.recipients || []);
      } catch (error) {
        console.error('❌ Error loading recipients:', error);
      } finally {
        setLoadingRecipients(false);
      }
    };

    fetchRecipients();
  }, [campaign._id]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Campaign Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircle size={24} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaign Information</h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-500">Name:</span>
                <p className="text-gray-900">{campaign.name}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Subject:</span>
                <p className="text-gray-900">{campaign.subject}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Status:</span>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                  {getStatusIcon(campaign.status)}
                  <span className="ml-1">{getStatusText(campaign.status)}</span>
                </span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Created:</span>
                <p className="text-gray-900">{new Date(campaign.createdAt).toLocaleString('en-US')}</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistics</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{campaign.totalRecipients}</div>
                <div className="text-sm text-gray-500">Total Recipients</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{campaign.sentCount || 0}</div>
                <div className="text-sm text-gray-500">Sent</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{campaign.failedCount || 0}</div>
                <div className="text-sm text-gray-500">Failed</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">{campaign.unsubscribeCount || 0}</div>
                <div className="text-sm text-gray-500">Unsubscribed</div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Content</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <pre className="whitespace-pre-wrap text-sm text-gray-700">{campaign.message}</pre>
          </div>
        </div>

        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recipients</h3>
          {loadingRecipients ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : recipients.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-center py-8">
                <p className="text-gray-500">No recipients found for this campaign</p>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4">
              {campaign.status === 'draft' && (
                <div className="mb-3 bg-blue-50 border border-blue-200 rounded p-2">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-4 w-4 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-2">
                      <p className="text-xs text-blue-700">
                        <strong>Draft Campaign:</strong> An email will be sent to these recipients (when the campaign is launched).
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                {recipients.map((recipient, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                    <div>
                      <span className="font-medium">{recipient.leadName}</span>
                      <span className="text-gray-500 ml-2">({recipient.email})</span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(recipient.status)}`}>
                      {getStatusText(recipient.status)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkEmail; 